/**
 * The whole document, for the save path.
 *
 * One decision, in one place. A large document answers from the model, with the editor's current part
 * folded in first; every other document answers from the editor exactly as it always did. The save path
 * itself has no branch and no knowledge of parts - which is the point: this is the only seam between
 * saving a document and loading one in parts.
 */
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';

import cleanForSave from '../../helpers/cleanForSave.helper';
import type { TTreeHeading } from '../../tiptap/nodes/heading/headingNumbering';
import { documentTitle, headingTree, wholeDoc, wholeDocumentHtml } from '../editor/controller';
import { trace } from '../trace';

export type TDocumentToSave = {
  /** What gets uploaded. */
  html: string;
  /** What every collector walks - references, blocks, template fields, citations. */
  doc: PMNode;
  title: string;
  /** The outline, covering the whole document. */
  headings: TTreeHeading[];
};

export const documentToSave = (
  editor: Editor,
  headingStart: number,
  /** The heading plugin's own tree, used when the document is not loaded in parts. */
  editorHeadings: TTreeHeading[]
): TDocumentToSave => {
  // First, and before anything below reads the document: this folds the editor's part back in, so the
  // metadata cannot be collected from a document that predates the last edit.
  const startedHtml = performance.now();
  // Cleaned here rather than by the caller, per element and cached: handed the whole document at once,
  // `cleanForSave` re-parses 1.67 MB on every autosave. The caller still calls it on the result - the
  // guards there find nothing left to strip and return without parsing.
  const html = wholeDocumentHtml(editor, cleanForSave);
  const htmlMs = performance.now() - startedHtml;

  /**
   * One decision for all four fields, from one identity check.
   *
   * Asking each of them separately let them disagree: if the model held a DIFFERENT document than
   * this editor, `html` came from that document while `doc` fell back to this editor - uploading one
   * document's content described by another's metadata.
   */
  const startedDoc = performance.now();
  const doc = html === null ? null : wholeDoc(editor);
  const docMs = performance.now() - startedDoc;
  if (html === null || !doc) {
    return {
      html: editor.getHTML(),
      doc: editor.state.doc,
      title: editor.state.doc.firstChild?.textContent.trim() ?? '',
      headings: editorHeadings,
    };
  }

  const startedHeadings = performance.now();
  const headings = headingTree(headingStart) ?? editorHeadings;
  trace('save:documentToSave', {
    htmlMs: Math.round(htmlMs),
    docMs: Math.round(docMs),
    headingsMs: Math.round(performance.now() - startedHeadings),
    htmlChars: html?.length ?? 0,
  });

  return {
    html,
    doc,
    // The editor's first node is the title only when the editor holds the first part.
    title: documentTitle() ?? '',
    // Re-derived rather than taken from the plugin's last run, which happened before the commit above:
    // a heading edited in the live part would otherwise save with its previous text.
    headings,
  };
};
